import React, { useState } from 'react';
import * as types from '../../types';

type ImageProps = {
  desiredWidth?: number | string;
  image: types.IImage;
  offset?: number;
  /**
   * Mark the image the user sees first (the LCP candidate). Every other image
   * defaults to lazy, which makes the browser queue the hero behind
   * everything else; this flips it to eager + fetchpriority=high.
   */
  priority?: boolean;
} & React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

export const Image = ({
  image,
  desiredWidth,
  priority = false,
  loading = priority ? 'eager' : 'lazy',
  ...extraProps
}: ImageProps) => {
  // Lowercase on purpose: react-dom 18 doesn't know the camelCase `fetchPriority`
  // prop and warns on it; the lowercase name passes through as a plain attribute.
  const priorityAttrs = priority ? { fetchpriority: 'high' } : {};

  // These can be overridden if there is an error loading a source. `avif` and
  // `webp` are independent so a 404 on one doesn't suppress the other.
  const [avifEnabled, setAvifEnabled] = useState(true);
  const [webpEnabled, setWebpEnabled] = useState(true);

  if (image.retinaWidth) {
    extraProps['width'] = image.retinaWidth / 2;
  }
  if (image.retinaHeight) {
    extraProps['height'] = image.retinaHeight / 2;
  }

  if (typeof desiredWidth === 'number' && image.retinaWidth && image.retinaHeight) {
    const aspectRatio = image.retinaWidth / image.retinaHeight;
    extraProps['width'] = desiredWidth;
    extraProps['height'] = Math.trunc(desiredWidth / aspectRatio);
  } else if (typeof desiredWidth === 'string') {
    extraProps['width'] = desiredWidth;
    extraProps['height'] = undefined;
  }

  const imageExtension = image.url.substr(image.url.lastIndexOf('.') + 1).toLowerCase();
  const isJpgOrPng = imageExtension === 'jpg' || imageExtension === 'jpeg' || imageExtension === 'png';
  const isRaster = isJpgOrPng || imageExtension === 'webp';

  // Build the descriptor srcSet in the image's own format, e.g.
  // "foo@1500w.jpg 1500w, foo@3000w.jpg 3000w". Empty when no descriptors.
  let srcSet = '';
  if (image.descriptors) {
    srcSet = image.descriptors
      .map((size) => `${image.url.replace(/(\.jpe?g|\.png|\.webp)$/i, `@${size}$1`)} ${size}`)
      .join(', ');
  }

  // Derive alternate-format srcSets from the multi-resolution srcSet (or the
  // single url). The replace is GLOBAL — a non-global swap only converted the
  // first descriptor, leaving larger candidates (e.g. `eating@4000w.jpg`)
  // pointing at the original format, so retina viewports downloaded the jpg
  // from inside the AVIF <source>.
  const base = srcSet || image.url;
  const swapFormat = (target: string) => base.replace(/(?:\.jpe?g|\.png|\.webp)/gi, target);

  // AVIF for any raster source. Committed `.avif` siblings exist for static
  // assets; if one is missing (e.g. a dynamic CDN image) it 404s and `onError`
  // drops to the next <source>.
  const avifUrl = avifEnabled && isRaster ? swapFormat('.avif') : '';
  // WebP only when the original isn't already webp (a webp original is served
  // by the <img> fallback / the original-format <source> below).
  const webpUrl = webpEnabled && isJpgOrPng ? swapFormat('.webp') : '';

  return (
    <picture
      onError={
        /* Force fallback toward the original image when a source fails to load — which
         may just be a 404 for the avif/webp when the image url is external to this app.
         Disable the newest-first format so the browser drops to the next <source>. */
        () => {
          if (avifEnabled) {
            setAvifEnabled(false);
          } else {
            setWebpEnabled(false);
          }
        }
      }
    >
      {avifUrl !== '' && <source srcSet={avifUrl} type="image/avif" />}
      {webpUrl !== '' && <source srcSet={webpUrl} type="image/webp" />}
      {srcSet !== '' && <source srcSet={srcSet} type={imageExtension === 'webp' ? 'image/webp' : 'image/jpeg'} />}
      <img src={image.url} alt="" loading={loading} {...priorityAttrs} {...extraProps} />
    </picture>
  );
};
